import { useEffect, useState } from "react";
import api, { formatBRL, formatDateTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import IndicarCelModal from "@/components/IndicarCelModal";
import { Sparkles, MapPin, TrendingUp, Award, Handshake, XCircle, Clock, User, DollarSign, ChevronDown, ChevronUp } from "lucide-react";

// Estágios em que o valor R$ é relevante (idem regra Dashboard CeL / Kanban)
const STAGES_COM_VALOR = new Set(["proposta", "documentacao", "contrato_assinado", "requalificado", "requalificacao"]);

const ESTAGIO_META = {
  // N1 (Prospecção/Fechamento)
  indicacao: { label: "Indicação", color: "bg-sky-100 text-sky-800 border-sky-300", icon: Sparkles },
  prospeccao: { label: "Prospecção", color: "bg-sky-100 text-sky-800 border-sky-300", icon: Sparkles },
  qualificado: { label: "Qualificado", color: "bg-cyan-100 text-cyan-800 border-cyan-300", icon: TrendingUp },
  reuniao: { label: "Reunião", color: "bg-indigo-100 text-indigo-800 border-indigo-300", icon: User },
  proposta: { label: "Proposta enviada", color: "bg-violet-100 text-violet-800 border-violet-300", icon: DollarSign },
  documentacao: { label: "Documentação", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  contrato_assinado: { label: "Contrato Assinado", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: Handshake },
  perdido: { label: "Perdido", color: "bg-slate-200 text-slate-600 border-slate-300", icon: XCircle },
  // N2 (Requalificação)
  entrada: { label: "Requalif. · Entrada", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", icon: TrendingUp },
  nutricao: { label: "Requalif. · Nutrição", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", icon: TrendingUp },
  monitoramento_gatilho: { label: "Requalif. · Mon. Gatilho", color: "bg-purple-100 text-purple-800 border-purple-300", icon: TrendingUp },
  gatilho_ativado: { label: "Requalif. · Gatilho Ativado", color: "bg-pink-100 text-pink-800 border-pink-300", icon: TrendingUp },
  requalificado: { label: "Requalificado", color: "bg-rose-100 text-rose-800 border-rose-300", icon: TrendingUp },
  // Legacy fallback (cards antigos ainda não migrados)
  indicacao_prospeccao: { label: "Indicação / Prospecção", color: "bg-sky-100 text-sky-800 border-sky-300", icon: Sparkles },
  qualificacao: { label: "Qualificação", color: "bg-cyan-100 text-cyan-800 border-cyan-300", icon: TrendingUp },
  requalificacao: { label: "Requalificação", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", icon: TrendingUp },
};

function StageBadge({ estagio }) {
  const meta = ESTAGIO_META[estagio] || { label: estagio, color: "bg-slate-100 text-slate-700 border-slate-300", icon: Sparkles };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-bold ${meta.color}`} data-testid={`badge-estagio-${estagio}`}>
      <Icon className="w-3.5 h-3.5" /> {meta.label}
    </span>
  );
}

function IndicacaoCard({ ind }) {
  const [expand, setExpand] = useState(false);
  const isFechado = ind.estagio === "contrato_assinado";
  const isPerdido = ind.estagio === "perdido";
  const historicoOrd = [...(ind.historico || [])].sort((a, b) => (b.data > a.data ? 1 : -1));
  return (
    <div className={`bg-white rounded-2xl border shadow-sm p-5 ${isFechado ? "border-emerald-300 ring-2 ring-emerald-200" : isPerdido ? "border-slate-300 opacity-70" : "border-slate-200"}`} data-testid={`indicacao-card-${ind.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <MapPin className="w-4 h-4 text-slate-400" />
            <div className="font-bold text-lg">{ind.municipio_nome} / {ind.uf}</div>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {ind.tipo_orgao || "—"} · Criado em {formatDateTime(ind.criado_em)}
          </div>
        </div>
        <StageBadge estagio={ind.estagio} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
        {STAGES_COM_VALOR.has(ind.estagio) && (
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Valor estimado</div>
            <div className="font-bold text-slate-800">{formatBRL(ind.valor_estimado)}</div>
          </div>
        )}
        {isFechado && (
          <>
            <div className="bg-emerald-50 rounded-lg p-2">
              <div className="text-[10px] uppercase text-emerald-700 font-bold tracking-wider">Valor fechado</div>
              <div className="font-bold text-emerald-800">{formatBRL(ind.valor_fechado)}</div>
            </div>
            <div className="bg-emerald-100 rounded-lg p-2">
              <div className="text-[10px] uppercase text-emerald-700 font-bold tracking-wider">Comissão ({ind.comissao_pct}%)</div>
              <div className="font-bold text-emerald-900 text-base">{formatBRL(ind.comissao_valor)}</div>
            </div>
          </>
        )}
        <div className="bg-slate-50 rounded-lg p-2">
          <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Responsável CeL</div>
          <div className="font-semibold text-slate-800">{ind.vendedora_nome || <span className="text-slate-400 italic">Aguardando</span>}</div>
        </div>
        {!isFechado && (
          <div className="bg-slate-50 rounded-lg p-2">
            <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Atualizado</div>
            <div className="font-semibold text-slate-800">{formatDateTime(ind.atualizado_em)}</div>
          </div>
        )}
      </div>

      {ind.contato_nome && (
        <div className="mt-3 text-xs text-slate-500">
          <b>Contato:</b> {ind.contato_nome}{ind.contato_cargo ? ` · ${ind.contato_cargo}` : ""}
        </div>
      )}

      <button
        onClick={() => setExpand((v) => !v)}
        className="mt-4 flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
        data-testid={`toggle-historico-${ind.id}`}
      >
        {expand ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        Histórico ({historicoOrd.length})
      </button>
      {expand && (
        <div className="mt-2 border-l-2 border-slate-200 pl-3 space-y-2">
          {historicoOrd.map((e, i) => (
            <div key={i} className="text-xs">
              <div className="text-slate-500">{formatDateTime(e.data)} · <b className="text-slate-700">{e.autor_nome}</b></div>
              <div className="text-slate-800">{e.descricao}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MinhasIndicacoes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/cel/indicacoes/minhas")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const isCelStaff = user?.role === "gestao_cel" || (user?.role === "vendedora" && user?.nucleo_nome === "CeL Consultoria");
  const podeCriar = !isCelStaff; // gestao_cel/vendedora CeL não indicam

  const kpi = {
    total: items.length,
    ativas: items.filter((i) => !["contrato_assinado", "perdido"].includes(i.estagio)).length,
    fechadas: items.filter((i) => i.estagio === "contrato_assinado").length,
    comissao: items.reduce((s, i) => s + (i.estagio === "contrato_assinado" ? (i.comissao_valor || 0) : 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="minhas-indicacoes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-sky-600" /> Minhas Indicações CeL
          </h1>
          <p className="text-sm text-slate-500 mt-1">Acompanhe em tempo real o status dos municípios que você indicou.</p>
        </div>
        {podeCriar && (
          <Button onClick={() => setOpenModal(true)} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="btn-nova-indicacao">
            <Sparkles className="w-4 h-4 mr-1" /> Nova indicação
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Total</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{kpi.total}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Em andamento</div>
          <div className="text-3xl font-bold text-sky-700 mt-1">{kpi.ativas}</div>
        </div>
        <div className="bg-white border border-emerald-300 rounded-2xl p-4 ring-2 ring-emerald-100">
          <div className="text-[10px] uppercase text-emerald-700 font-bold tracking-wider flex items-center gap-1"><Award className="w-3 h-3" /> Contratos fechados</div>
          <div className="text-3xl font-bold text-emerald-800 mt-1">{kpi.fechadas}</div>
        </div>
        <div className="bg-emerald-600 text-white rounded-2xl p-4 shadow-lg">
          <div className="text-[10px] uppercase font-bold tracking-wider opacity-90">Comissão acumulada</div>
          <div className="text-3xl font-bold mt-1" data-testid="kpi-comissao-total">{formatBRL(kpi.comissao)}</div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12 text-slate-400 text-sm">Carregando indicações...</div>
      )}

      {!loading && items.length === 0 && (
        <div className="bg-white border-2 border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <div className="text-lg font-semibold text-slate-700">Você ainda não tem indicações</div>
          <p className="text-sm text-slate-500 mt-1">Indique um município e receba comissão a cada contrato fechado pela CeL Consultoria.</p>
          {podeCriar && (
            <Button onClick={() => setOpenModal(true)} className="mt-4 bg-sky-600 hover:bg-sky-700 text-white" data-testid="btn-primeira-indicacao">
              <Sparkles className="w-4 h-4 mr-1" /> Criar primeira indicação
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((i) => <IndicacaoCard key={i.id} ind={i} />)}
      </div>

      <IndicarCelModal open={openModal} onClose={() => setOpenModal(false)} onCreated={load} />
    </div>
  );
}
